<template>
  <div>
    <CRow>
      <!-- 左侧：路由器列表 -->
      <CCol md="3" lg="3">
        <CCard>
          <CCardHeader>
            <strong>路由器</strong>
            <CBadge color="secondary" class="float-right">{{ routerList.length }}</CBadge>
          </CCardHeader>
          <CCardBody class="p-0">
            <div v-if="!routerList.length" class="text-center text-muted py-4">
              暂无可用路由器
            </div>
            <div v-else class="router-list">
              <div
                v-for="r in routerList"
                :key="r.id"
                class="router-item"
                :class="{ active: selectedRouterId === r.id }"
                @click="selectRouter(r.id)"
              >
                <div class="router-name">
                  <CIcon name="cil-router" size="sm" class="mr-1" />{{ r.name }}
                </div>
                <div class="router-host">{{ r.host }}</div>
              </div>
            </div>
          </CCardBody>
        </CCard>
      </CCol>

      <!-- 右侧：Filter 状态内容 -->
      <CCol md="9" lg="9">
        <CCard>
          <CCardHeader>
            <strong>实际 Filter 状态</strong>
            <span v-if="currentRouter" class="text-muted ml-2">
              {{ currentRouter.name }} ({{ currentRouter.host }})
            </span>
            <CButton
              color="info"
              size="sm"
              class="float-right"
              @click="refresh"
              :disabled="!selectedRouterId || refreshing"
            >
              <CIcon name="cil-sync" /> {{ refreshing ? '拉取中...' : '拉取最新' }}
            </CButton>
            <CButton
              color="success"
              size="sm"
              class="float-right mr-2"
              variant="outline"
              @click="openExtractModal"
              :disabled="!selectedRouterId || !stateList.length"
            >
              <CIcon name="cil-cloud-download" /> 抽取为模板
            </CButton>
          </CCardHeader>

          <CCardBody>
            <div v-if="!selectedRouterId" class="text-center text-muted py-5">
              <CIcon name="cil-arrow-left" size="xl" />
              <div class="mt-2">请先在左侧选择一台路由器</div>
            </div>

            <template v-else>
              <!-- 统计卡片 -->
              <CRow class="mb-3">
                <CCol col="6" md="2">
                  <div class="stat-box">
                    <div class="stat-num">{{ summary.total }}</div>
                    <div class="stat-label">规则总数</div>
                  </div>
                </CCol>
                <CCol col="6" md="2">
                  <div class="stat-box text-success">
                    <div class="stat-num">{{ summary.enabled }}</div>
                    <div class="stat-label">启用</div>
                  </div>
                </CCol>
                <CCol col="6" md="2">
                  <div class="stat-box text-secondary">
                    <div class="stat-num">{{ summary.disabled }}</div>
                    <div class="stat-label">禁用</div>
                  </div>
                </CCol>
                <CCol col="6" md="2">
                  <div class="stat-box text-info">
                    <div class="stat-num">{{ summary.managed }}</div>
                    <div class="stat-label">系统托管</div>
                  </div>
                </CCol>
                <CCol col="6" md="2">
                  <div class="stat-box text-muted">
                    <div class="stat-num">{{ summary.manual }}</div>
                    <div class="stat-label">手动规则</div>
                  </div>
                </CCol>
                <CCol col="6" md="2">
                  <div class="stat-box">
                    <div class="stat-time">{{ fetchedAtText }}</div>
                    <div class="stat-label">最后抓取</div>
                  </div>
                </CCol>
              </CRow>

              <!-- chain 筛选 -->
              <div class="mb-3">
                <span class="mr-2 text-muted">Chain 筛选：</span>
                <CButton
                  size="sm"
                  class="mr-1 mb-1"
                  :color="chainFilter === '' ? 'primary' : 'light'"
                  @click="chainFilter = ''"
                >
                  全部 ({{ summary.total }})
                </CButton>
                <CButton
                  v-for="c in chains"
                  :key="c.name"
                  size="sm"
                  class="mr-1 mb-1"
                  :color="chainFilter === c.name ? 'primary' : 'light'"
                  @click="chainFilter = c.name"
                >
                  {{ c.name }} ({{ c.count }})
                </CButton>
              </div>

              <CDataTable
                :items="filteredList"
                :fields="fields"
                :loading="loading"
                :items-per-page="15"
                :table-filter="{ label: '搜索规则:', placeholder: '输入关键字过滤规则内容' }"
                :sorter="{ external: false, resettable: true }"
                pagination
                hover
                striped
                column-filter
              >
                <template #seq="{ item }">
                  <td class="text-center text-muted">{{ item.seq }}</td>
                </template>

                <template #chain_name="{ item }">
                  <td><CBadge color="dark">{{ item.chain_name }}</CBadge></td>
                </template>

                <template #rule_script="{ item }">
                  <td>
                    <code class="rule-code" :class="{ 'text-muted': item.disabled }">{{ item.rule_script || '(空)' }}</code>
                    <div v-if="item.comment_raw" class="rule-comment">
                      <CIcon name="cil-comment-square" size="sm" /> {{ item.comment_raw }}
                    </div>
                  </td>
                </template>

                <template #disabled="{ item }">
                  <td>
                    <CBadge :color="item.disabled ? 'secondary' : 'success'">
                      {{ item.disabled ? '禁用' : '启用' }}
                    </CBadge>
                  </td>
                </template>

                <template #scope="{ item }">
                  <td>
                    <CBadge :color="scopeColor(item.scope)">{{ scopeLabel(item.scope) }}</CBadge>
                  </td>
                </template>

                <template #no-items-view>
                  <div class="text-center text-muted py-4">
                    该路由器暂无 filter 数据，请点击右上角「拉取最新」
                  </div>
                </template>
              </CDataTable>
            </template>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <!-- 抽取为模板 -->
    <CModal title="从路由器抽取为 Filter 模板" color="primary" :show.sync="extractModal.show" size="lg">
      <div v-if="currentRouter" class="mb-3 text-muted">
        源路由器：<strong>{{ currentRouter.name }}</strong>（{{ currentRouter.host }}）
      </div>

      <CForm>
        <CRow>
          <CCol md="6">
            <CInput
              label="模板名称"
              placeholder="给这个模板起个名字"
              v-model="extractModal.form.name"
              :is-valid="extractModal.form.name ? true : null"
            />
          </CCol>
          <CCol md="6">
            <CSelect
              label="抽取的 Chain"
              :options="extractChainOptions"
              :value.sync="extractModal.form.chain_name"
            />
          </CCol>
        </CRow>

        <CRow>
          <CCol md="4">
            <CSelect
              label="Scope"
              :options="[
                { value: 'local', label: 'Local（仅同步到源路由器）' },
                { value: 'global', label: 'Global（同步到所有路由器）' }
              ]"
              :value.sync="extractModal.form.scope"
            />
          </CCol>
          <CCol md="4">
            <CSelect
              label="AFI"
              :options="['ipv4', 'ipv6', 'dual']"
              :value.sync="extractModal.form.afi"
            />
          </CCol>
          <CCol md="4">
            <CSelect
              label="方向"
              :options="[
                { value: 'both', label: 'both' },
                { value: 'in', label: 'in' },
                { value: 'out', label: 'out' }
              ]"
              :value.sync="extractModal.form.direction"
            />
          </CCol>
        </CRow>

        <CRow>
          <CCol md="6">
            <CSelect
              label="协议"
              :options="['mixed', 'bgp', 'ospf', 'rip', 'static']"
              :value.sync="extractModal.form.protocol"
            />
          </CCol>
          <CCol md="6">
            <CInput
              label="描述（可选）"
              placeholder="不填则自动生成"
              v-model="extractModal.form.description"
            />
          </CCol>
        </CRow>

        <div class="mt-2">
          <CInputCheckbox
            label="包含禁用规则"
            :checked.sync="extractModal.form.include_disabled"
            custom
          />
          <CInputCheckbox
            class="mt-2"
            label="包含已托管规则（默认仅抽取手动规则）"
            :checked.sync="extractModal.form.include_managed"
            custom
          />
        </div>

        <CAlert color="info" class="mt-3 mb-0">
          将从该路由器已抓取的实际状态中抽取 <strong>{{ extractPreviewCount }}</strong> 条规则生成模板。
          若数据不是最新，请先「拉取最新」。
        </CAlert>
      </CForm>

      <template #footer>
        <CButton color="secondary" @click="extractModal.show = false">取消</CButton>
        <CButton
          color="success"
          @click="doExtract"
          :disabled="extractModal.saving || !extractModal.form.name || !extractModal.form.chain_name"
        >
          {{ extractModal.saving ? '抽取中...' : '确认抽取' }}
        </CButton>
      </template>
    </CModal>

    <CModal title="提示" color="primary" :show.sync="msgModal" size="sm">
      {{ msg }}
      <template #footer>
        <CButton color="primary" @click="msgModal = false">确定</CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
export default {
  name: 'RoutingRouterStates',
  data() {
    return {
      loading: false,
      refreshing: false,
      msg: '',
      msgModal: false,
      selectedRouterId: null,
      routerList: [],
      stateList: [],
      chains: [],
      chainFilter: '',
      fetchedAt: 0,
      summary: { total: 0, enabled: 0, disabled: 0, managed: 0, manual: 0 },
      extractModal: {
        show: false,
        saving: false,
        form: {
          name: '',
          chain_name: '',
          scope: 'local',
          afi: 'ipv4',
          protocol: 'mixed',
          direction: 'both',
          description: '',
          include_disabled: true,
          include_managed: false
        }
      },
      fields: [
        { key: 'seq', label: '#', filter: false, sorter: false, _style: 'width:48px' },
        { key: 'chain_name', label: 'Chain', _style: 'width:220px' },
        { key: 'rule_script', label: '规则内容' },
        { key: 'disabled', label: '状态', filter: false, _style: 'width:90px' },
        { key: 'scope', label: '类型', filter: false, _style: 'width:110px' }
      ]
    }
  },
  computed: {
    currentRouter() {
      return this.routerList.find(r => r.id === this.selectedRouterId) || null
    },
    filteredList() {
      if (!this.chainFilter) return this.stateList
      return this.stateList.filter(s => s.chain_name === this.chainFilter)
    },
    fetchedAtText() {
      if (!this.fetchedAt) return '—'
      const d = new Date(this.fetchedAt * 1000)
      const p = n => (n < 10 ? '0' + n : '' + n)
      return `${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`
    },
    // 抽取弹窗可选的 chain 列表
    extractChainOptions() {
      return this.chains.map(c => ({ value: c.name, label: `${c.name} (${c.count})` }))
    },
    // 按当前选项预估将抽取的规则条数
    extractPreviewCount() {
      const f = this.extractModal.form
      if (!f.chain_name) return 0
      return this.stateList.filter(s => {
        if (s.chain_name !== f.chain_name) return false
        if (!f.include_disabled && s.disabled) return false
        if (!f.include_managed && (s.scope && s.scope !== 'manual')) return false
        if (!s.rule_script) return false
        return true
      }).length
    }
  },
  watch: {
    selectedRouterId(newVal) {
      this.chainFilter = ''
      if (newVal) this.loadStates()
    }
  },
  mounted() {
    this.loadRouters()
  },
  methods: {
    async loadRouters() {
      try {
        const res = await this.$axios.get('user/router')
        if (res.data.code === 200) {
          const own = res.data.userRouter || []
          const authed = (res.data.authedRouter || []).map(a => a.router).filter(Boolean)
          this.routerList = [...own, ...authed]
        }
      } catch (e) { /* ignore */ }
    },
    selectRouter(id) {
      if (this.selectedRouterId === id) return
      this.selectedRouterId = id
    },
    notify(text) {
      this.msg = text
      this.msgModal = true
    },
    async loadStates() {
      if (!this.selectedRouterId) return
      this.loading = true
      try {
        const res = await this.$axios.get('user/routing/router-filters', {
          params: { router_id: this.selectedRouterId }
        })
        if (res.data.code === 200) {
          this.stateList = res.data.data || []
          this.chains = res.data.chains || []
          this.fetchedAt = res.data.fetched_at || 0
          this.summary = res.data.summary || { total: 0, enabled: 0, disabled: 0, managed: 0, manual: 0 }
        } else {
          this.notify(res.data.msg || '加载失败')
        }
      } catch (e) {
        this.notify('网络错误: ' + e.message)
      } finally {
        this.loading = false
      }
    },
    async refresh() {
      if (!this.selectedRouterId) return
      this.refreshing = true
      try {
        const res = await this.$axios.post(`user/routing/router-filters/${this.selectedRouterId}/refresh`)
        if (res.data.code === 200) {
          await this.loadStates()
          this.notify(`已拉取 ${res.data.data.count} 条规则`)
        } else {
          this.notify(res.data.msg || '拉取失败')
        }
      } catch (e) {
        this.notify('网络错误: ' + e.message)
      } finally {
        this.refreshing = false
      }
    },
    scopeColor(s) {
      return { global: 'info', local: 'warning', manual: 'light' }[s] || 'light'
    },
    scopeLabel(s) {
      return { global: '全局托管', local: '本机托管', manual: '手动' }[s] || '手动'
    },
    openExtractModal() {
      if (!this.selectedRouterId || !this.stateList.length) return
      // 默认抽取当前筛选的 chain；未筛选则取第一个 chain
      const defaultChain = this.chainFilter || (this.chains[0] && this.chains[0].name) || ''
      const routerName = this.currentRouter ? this.currentRouter.name : ''
      this.extractModal.form = {
        name: defaultChain ? `${routerName}-${defaultChain}` : '',
        chain_name: defaultChain,
        scope: 'local',
        afi: 'ipv4',
        protocol: 'mixed',
        direction: 'both',
        description: '',
        include_disabled: true,
        include_managed: false
      }
      this.extractModal.show = true
    },
    async doExtract() {
      const f = this.extractModal.form
      if (!f.name || !f.chain_name) return
      this.extractModal.saving = true
      try {
        const res = await this.$axios.post('user/routing/filter/chains/extract-from-router', {
          router_id: this.selectedRouterId,
          chain_name: f.chain_name,
          name: f.name,
          scope: f.scope,
          afi: f.afi,
          protocol: f.protocol,
          direction: f.direction,
          description: f.description,
          include_disabled: f.include_disabled ? 1 : 0,
          include_managed: f.include_managed ? 1 : 0
        })
        if (res.data.code === 200) {
          this.extractModal.show = false
          this.notify(`已抽取 ${res.data.data.rule_count} 条规则，生成模板「${res.data.data.name}」`)
        } else {
          this.notify(res.data.msg || '抽取失败')
        }
      } catch (e) {
        this.notify('网络错误: ' + e.message)
      } finally {
        this.extractModal.saving = false
      }
    }
  }
}
</script>

<style scoped>
.stat-box {
  border: 1px solid #ebedef;
  border-radius: 6px;
  padding: 10px 8px;
  text-align: center;
  height: 100%;
}
.stat-num {
  font-size: 1.6rem;
  font-weight: 600;
  line-height: 1.2;
}
.stat-time {
  font-size: 1rem;
  font-weight: 600;
  line-height: 2;
}
.stat-label {
  font-size: 0.75rem;
  color: #768192;
}
.rule-code {
  display: block;
  white-space: pre-wrap;
  word-break: break-all;
  color: #e83e8c;
}
.rule-comment {
  font-size: 0.75rem;
  color: #768192;
  margin-top: 2px;
}
.router-list {
  max-height: calc(100vh - 200px);
  overflow-y: auto;
}
.router-item {
  padding: 10px 14px;
  border-bottom: 1px solid #ebedef;
  cursor: pointer;
  border-left: 3px solid transparent;
  transition: background 0.15s, border-color 0.15s;
}
.router-item:hover {
  background: #f0f4f7;
}
.router-item.active {
  background: #ebf5ff;
  border-left-color: #321fdb;
}
.router-name {
  font-weight: 600;
  color: #3c4b64;
}
.router-item.active .router-name {
  color: #321fdb;
}
.router-host {
  font-size: 0.75rem;
  color: #768192;
  margin-top: 2px;
}

/* ===== 抽取弹窗 ===== */
.extract-modal {
  border: none;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.18);
}
/* header */
.ex-header {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: linear-gradient(135deg, #2eb85c 0%, #1b9e4b 100%);
  color: #fff;
}
.ex-header-title {
  font-size: 1.05rem;
  font-weight: 600;
  display: flex;
  align-items: center;
}
.ex-close {
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.85);
  font-size: 1.6rem;
  line-height: 1;
  cursor: pointer;
  padding: 0 4px;
  transition: color 0.15s;
}
.ex-close:hover {
  color: #fff;
}
/* 源路由器横幅 */
.ex-source {
  display: flex;
  align-items: center;
  background: #f4f9f6;
  border: 1px solid #d8ede1;
  border-radius: 10px;
  padding: 12px 16px;
  margin-bottom: 18px;
}
.ex-source-icon {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  background: #2eb85c;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
}
.ex-source-info {
  flex: 1;
}
.ex-source-name {
  font-weight: 600;
  font-size: 1rem;
  color: #2a2f36;
}
.ex-source-host {
  font-size: 0.8rem;
  color: #768192;
}
.ex-source-count {
  text-align: center;
  padding-left: 16px;
  border-left: 1px dashed #c6e2d2;
}
.ex-count-num {
  font-size: 1.5rem;
  font-weight: 700;
  color: #2eb85c;
  line-height: 1.1;
}
.ex-count-label {
  font-size: 0.7rem;
  color: #768192;
}
/* 分区 */
.ex-section {
  margin-bottom: 18px;
}
.ex-section-title {
  font-size: 0.8rem;
  font-weight: 600;
  color: #8a93a2;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 10px;
  padding-left: 10px;
  border-left: 3px solid #2eb85c;
}
.ex-row {
  margin-bottom: 0;
}
.ex-label {
  font-size: 0.82rem;
  font-weight: 500;
  color: #4f5d73;
  margin-bottom: 4px;
  display: block;
}
.ex-optional {
  font-size: 0.72rem;
  color: #a4adb9;
  font-weight: 400;
}
.ex-input >>> .form-control,
.ex-input >>> .custom-select {
  border-radius: 8px;
}
/* scope 卡片 */
.ex-scope-cards {
  display: flex;
  gap: 12px;
}
.ex-scope-card {
  flex: 1;
  border: 1.5px solid #e3e6ec;
  border-radius: 10px;
  padding: 12px 14px;
  cursor: pointer;
  transition: all 0.15s;
  background: #fff;
}
.ex-scope-card:hover {
  border-color: #b7d9c5;
  background: #fafdfb;
}
.ex-scope-card.active {
  border-color: #2eb85c;
  background: #f1faf4;
  box-shadow: 0 0 0 3px rgba(46, 184, 92, 0.12);
}
.ex-scope-head {
  font-weight: 600;
  color: #3c4b64;
  display: flex;
  align-items: center;
}
.ex-scope-card.active .ex-scope-head {
  color: #1b9e4b;
}
.ex-scope-desc {
  font-size: 0.76rem;
  color: #768192;
  margin-top: 2px;
}
/* checkbox */
.ex-check {
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-bottom: 10px;
  font-size: 0.9rem;
  color: #3c4b64;
  user-select: none;
}
.ex-check:last-child {
  margin-bottom: 0;
}
.ex-check input {
  display: none;
}
.ex-check-box {
  width: 18px;
  height: 18px;
  border: 1.5px solid #c6cbd4;
  border-radius: 5px;
  margin-right: 10px;
  position: relative;
  transition: all 0.15s;
  flex-shrink: 0;
}
.ex-check.on .ex-check-box {
  background: #2eb85c;
  border-color: #2eb85c;
}
.ex-check.on .ex-check-box::after {
  content: '';
  position: absolute;
  left: 5px;
  top: 1px;
  width: 5px;
  height: 10px;
  border: solid #fff;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}
/* footer */
.ex-footer {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  background: #f8f9fb;
  border-top: 1px solid #ebedef;
}
.ex-footer-hint {
  font-size: 0.85rem;
  color: #768192;
}
.ex-footer-hint strong {
  color: #2eb85c;
  font-size: 1.05rem;
}
</style>
